/**
 * assets/modal-engine.js
 *
 * Global Video Hub — Modal / Commerce / Review Engine
 * Version: 5.1
 *
 * Features:
 * - Modal lifecycle
 * - Focus management
 * - YouTube / MP4 playback
 * - Product / Variant
 * - AJAX Add to Cart
 * - Shopify official payment button activation
 * - Rating summary
 * - Review preview with third-party adapter architecture
 */

(function () {
  'use strict';

  /* =========================================================
     CONSTANTS
  ========================================================= */

  var CARD_SELECTOR = '[data-video-modal-trigger]';

  var FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  var MOBILE_BREAKPOINT = 749;

  var productCache = new Map();
  var requestSerial = 0;
  var activeCard = null;
  var ytPlayer = null;
  var ytApiPromise = null;
  var currentModal = null;


  /* =========================================================
     MODAL DOM
  ========================================================= */

  function getModalElements() {
    var modals = document.querySelectorAll('[data-modal-root]');

    if (!modals.length) {
      return null;
    }

    var root = modals[0];

    for (var i = 1; i < modals.length; i++) {
      modals[i].setAttribute('aria-hidden', 'true');
      modals[i].classList.add('ugc-modal-duplicate');
    }

    return {
      root: root,
      inner: root.querySelector('.ugc-modal-inner'),
      videoFrame: root.querySelector('[data-ugc-video-frame]') || root.querySelector('#ugc-modal-video-frame'),
      productColumn: root.querySelector('#ugc-modal-product-col'),
      productLoading: root.querySelector('#ugc-modal-product-loading'),
      productContent: root.querySelector('#ugc-modal-product-content'),
      closeButton: root.querySelector('[data-ugc-modal-close]') || root.querySelector('#ugc-modal-close')
    };
  }

  function ensureModal() {
    if (currentModal && currentModal.root && document.documentElement.contains(currentModal.root)) {
      return currentModal;
    }
    currentModal = getModalElements();
    return currentModal;
  }


  /* =========================================================
     INITIALIZATION
  ========================================================= */

  function initialize() {
    var modal = ensureModal();

    if (!modal || !modal.root || !modal.videoFrame) {
      console.warn('[UGC Modal] Modal DOM not found.');
      return;
    }

    if (!document.documentElement.dataset.ugcModalEngineBound) {
      document.documentElement.dataset.ugcModalEngineBound = 'true';

      document.addEventListener('click', handleDocumentClick);
      document.addEventListener('keydown', handleDocumentKeydown);
      document.addEventListener('shopify:section:load', handleThemeSectionLoad);
      document.addEventListener('shopify:section:unload', handleThemeSectionUnload);
    }
  }


  /* =========================================================
     GLOBAL EVENTS
  ========================================================= */

  function handleDocumentClick(event) {
    var modal = ensureModal();
    if (!modal) return;

    var target = event.target;
    if (!(target instanceof Element)) return;

    var close = target.closest('[data-ugc-modal-close]');
    if (close && modal.root.contains(close)) {
      event.preventDefault();
      closeModal();
      return;
    }

    var card = target.closest(CARD_SELECTOR);
    if (card) {
      event.preventDefault();
      openModal(card);
      return;
    }

    if (target === modal.root && modal.root.classList.contains('ugc-modal-open')) {
      closeModal();
    }
  }

  function handleDocumentKeydown(event) {
    var modal = ensureModal();
    if (!modal) return;

    var isOpen = modal.root.classList.contains('ugc-modal-open');

    if (!isOpen && (event.key === 'Enter' || event.key === ' ')) {
      var target = event.target;
      if (!(target instanceof Element)) return;

      var card = target.closest(CARD_SELECTOR);
      if (card) {
        event.preventDefault();
        openModal(card);
      }
      return;
    }

    if (!isOpen) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      closeModal();
      return;
    }

    if (event.key === 'Tab') {
      trapFocus(event, modal);
    }
  }


  /* =========================================================
     OPEN MODAL
  ========================================================= */

  async function openModal(card) {
    var modal = ensureModal();
    if (!modal) return;

    var requestId = ++requestSerial;
    activeCard = card;

    clearPlayingCards();
    card.classList.add('is-playing');

    var data = readCardData(card);

    modal.root.removeAttribute('aria-hidden');
    modal.root.classList.add('ugc-modal-open');
    document.body.classList.add('ugc-body-no-scroll');

    showProductLoading(modal);

    if (data.videoType === 'youtube' && data.youtubeId) {
      playYouTube(data.youtubeId, modal.videoFrame);
    } else if (data.videoUrl) {
      playMP4(data.videoUrl, modal.videoFrame);
    } else {
      renderVideoUnavailable(modal.videoFrame);
    }

    window.requestAnimationFrame(function () {
      if (modal.closeButton) {
        try {
          modal.closeButton.focus({ preventScroll: true });
        } catch (_) {
          modal.closeButton.focus();
        }
      }
    });

    var product = await fetchProductData(data.productHandle);

    if (requestId !== requestSerial || !isModalOpen()) {
      return;
    }

    renderProductPanel(modal, product, data, requestId);
    hydrateReviewExperience(modal, product, data, requestId);

    dispatchUGCEvent('ugc:video:open', {
      videoTitle: data.videoTitle,
      videoType: data.videoType,
      productHandle: data.productHandle,
      productId: data.productId,
      variantId: data.variantId
    });
  }

  function readCardData(card) {
    return {
      videoUrl: card.dataset.videoUrl || '',
      videoType: card.dataset.videoType || 'mp4',
      youtubeId: card.dataset.youtubeId || '',
      videoTitle: card.dataset.videoTitle || '',
      productHandle: card.dataset.productHandle || '',
      productId: card.dataset.productId || '',
      variantId: card.dataset.variantId || '',
      rating: parsePositiveFloat(card.dataset.rating),
      ratingCount: parsePositiveInteger(card.dataset.ratingCount),
      ratingScaleMax: parsePositiveFloat(card.dataset.ratingScaleMax) || 5
    };
  }


  /* =========================================================
     CLOSE MODAL
  ========================================================= */

  function closeModal() {
    var modal = ensureModal();
    if (!modal) return;

    if (!modal.root.classList.contains('ugc-modal-open')) {
      return;
    }

    requestSerial++;

    modal.root.setAttribute('aria-hidden', 'true');
    modal.root.classList.remove('ugc-modal-open');
    document.body.classList.remove('ugc-body-no-scroll');

    destroyCurrentPlayer(modal.videoFrame);
    clearPlayingCards();
    showProductLoading(modal);

    var restoreTarget = activeCard;
    activeCard = null;

    if (restoreTarget && document.documentElement.contains(restoreTarget)) {
      window.requestAnimationFrame(function () {
        try {
          restoreTarget.focus({ preventScroll: true });
        } catch (_) {
          restoreTarget.focus();
        }
      });
    }

    dispatchUGCEvent('ugc:modal:close', {});
  }

  function isModalOpen() {
    var modal = ensureModal();
    return Boolean(modal && modal.root && modal.root.classList.contains('ugc-modal-open'));
  }

  function clearPlayingCards() {
    var cards = document.querySelectorAll(CARD_SELECTOR + '.is-playing');
    for (var i = 0; i < cards.length; i++) {
      cards[i].classList.remove('is-playing');
    }
  }


  /* =========================================================
     FOCUS MANAGEMENT
  ========================================================= */

  function trapFocus(event, modal) {
    if (!modal.inner) return;

    var elements = modal.inner.querySelectorAll(FOCUSABLE_SELECTOR);
    var focusable = [];

    for (var i = 0; i < elements.length; i++) {
      if (isFocusable(elements[i])) {
        focusable.push(elements[i]);
      }
    }

    if (!focusable.length) {
      event.preventDefault();
      if (modal.closeButton) {
        modal.closeButton.focus();
      }
      return;
    }

    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    var active = document.activeElement;

    if (event.shiftKey) {
      if (active === first || !modal.inner.contains(active)) {
        event.preventDefault();
        last.focus();
      }
    } else {
      if (active === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  function isFocusable(element) {
    if (!element) return false;
    if (element.hidden) return false;
    if (element.getAttribute('aria-hidden') === 'true') return false;
    if (element instanceof HTMLButtonElement && element.disabled) return false;
    return element.getClientRects().length > 0;
  }


  /* =========================================================
     YOUTUBE
  ========================================================= */

  function loadYouTubeAPI() {
    if (window.YT && typeof window.YT.Player === 'function') {
      return Promise.resolve(window.YT);
    }

    if (ytApiPromise) {
      return ytApiPromise;
    }

    ytApiPromise = new Promise(function (resolve, reject) {
      var existingCallback = window.onYouTubeIframeAPIReady;

      window.onYouTubeIframeAPIReady = function () {
        if (typeof existingCallback === 'function') {
          try {
            existingCallback();
          } catch (_) {}
        }

        if (window.YT && typeof window.YT.Player === 'function') {
          resolve(window.YT);
        } else {
          reject(new Error('YouTube API loaded without Player'));
        }
      };

      var existingScript = document.querySelector('script[src*="youtube.com/iframe_api"]');

      if (!existingScript) {
        var script = document.createElement('script');
        script.src = 'https://www.youtube.com/iframe_api';
        script.async = true;
        script.onerror = function () {
          ytApiPromise = null;
          reject(new Error('YouTube API failed to load'));
        };
        document.head.appendChild(script);
      }

      var checks = 0;
      var timer = window.setInterval(function () {
        checks++;
        if (window.YT && typeof window.YT.Player === 'function') {
          window.clearInterval(timer);
          resolve(window.YT);
        }
        if (checks > 100) {
          window.clearInterval(timer);
        }
      }, 100);
    });

    return ytApiPromise;
  }

  async function playYouTube(videoId, frame) {
    destroyCurrentPlayer(frame);

    var sessionId = requestSerial;
    var holder = document.createElement('div');
    holder.className = 'ugc-yt-player';
    frame.replaceChildren(holder);

    try {
      var YTApi = await loadYouTubeAPI();

      if (sessionId !== requestSerial || !isModalOpen()) {
        return;
      }

      ytPlayer = new YTApi.Player(holder, {
        videoId: videoId,
        width: '100%',
        height: '100%',
        playerVars: {
          autoplay: 1,
          playsinline: 1,
          controls: 1,
          rel: 0,
          modestbranding: 1
        },
        events: {
          onReady: function (event) {
            try {
              event.target.playVideo();
            } catch (_) {}
          }
        }
      });
    } catch (error) {
      console.warn('[UGC Modal] YouTube player failed:', error);
      if (sessionId === requestSerial && isModalOpen()) {
        renderVideoUnavailable(frame);
      }
    }
  }


  /* =========================================================
     MP4
  ========================================================= */

  function playMP4(src, frame) {
    destroyCurrentPlayer(frame);

    var video = document.createElement('video');
    video.className = 'ugc-modal-native-video';
    video.src = src;
    video.controls = true;
    video.autoplay = true;
    video.playsInline = true;
    video.preload = 'metadata';
    video.setAttribute('playsinline', '');

    frame.appendChild(video);

    var promise;
    try {
      promise = video.play();
    } catch (_) {
      promise = null;
    }

    if (promise && typeof promise.catch === 'function') {
      promise.catch(function () {
        video.muted = true;
        var mutedPlay = video.play();
        if (mutedPlay && typeof mutedPlay.catch === 'function') {
          mutedPlay.catch(function () {});
        }
      });
    }
  }

  function destroyCurrentPlayer(frame) {
    if (ytPlayer) {
      try {
        if (typeof ytPlayer.destroy === 'function') {
          ytPlayer.destroy();
        }
      } catch (_) {}
      ytPlayer = null;
    }

    if (!frame) return;

    var videos = frame.querySelectorAll('video');
    for (var i = 0; i < videos.length; i++) {
      try {
        videos[i].pause();
        videos[i].removeAttribute('src');
        videos[i].load();
      } catch (_) {}
    }

    frame.replaceChildren();
  }

  function renderVideoUnavailable(frame) {
    if (!frame) return;
    frame.innerHTML = '<div class="ugc-video-unavailable"><p>Video unavailable.</p></div>';
  }


  /* =========================================================
     PRODUCT API
  ========================================================= */

  async function fetchProductData(handle) {
    if (!handle) return null;

    if (productCache.has(handle)) {
      return productCache.get(handle);
    }

    try {
      var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/';
      var url = root + 'products/' + encodeURIComponent(handle) + '.js';

      var response = await fetch(url, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      });

      if (!response.ok) {
        throw new Error('HTTP ' + response.status);
      }

      var product = await response.json();
      productCache.set(handle, product);
      return product;
    } catch (error) {
      console.warn('[UGC Modal] Product fetch failed:', error);
      return null;
    }
  }


  /* =========================================================
     PRODUCT PANEL
  ========================================================= */

  function renderProductPanel(modal, product, cardData, requestId) {
    var content = modal.productContent;
    if (!content) return;

    if (!product) {
      content.innerHTML = '<div class="ugc-no-product"><p>Product information is unavailable.</p></div>';
      showProductContent(modal);
      return;
    }

    var variant = findVariant(product, cardData.variantId);

    if (!variant) {
      content.innerHTML = '<div class="ugc-no-product"><p>This product is currently unavailable.</p></div>';
      showProductContent(modal);
      return;
    }

    var productUrl = '/products/' + encodeURIComponent(product.handle);
    var imageUrl = getProductImage(product, variant);
    var pricingHtml = buildPricingHtml(variant);
    var variantSelector = buildVariantSelector(product.variants || [], variant.id);

    var ratingHtml = '<div class="ugc-rating-summary" data-ugc-rating-summary hidden></div>';
    var reviewPreviewHtml = '<section class="ugc-review-preview" data-ugc-review-preview aria-label="Customer reviews" hidden></section>';

    var imageHtml = '';
    if (imageUrl) {
      imageHtml =
        '<div class="ugc-product-image-wrap">' +
          '<a href="' + escapeAttribute(productUrl) + '" class="ugc-product-image-link">' +
            '<img src="' + escapeAttribute(imageUrl) + '" alt="' + escapeAttribute(product.title) + '" class="ugc-product-image" loading="eager" decoding="async">' +
          '</a>' +
        '</div>';
    }

    var buttonHtml = buildPurchaseButton(variant);

    content.innerHTML =
      '<div class="ugc-product-panel">' +
        '<div class="ugc-product-header">' +
          imageHtml +
          '<div class="ugc-product-main-info">' +
            '<a href="' + escapeAttribute(productUrl) + '" class="ugc-product-title-link">' +
              '<h4 class="ugc-product-title">' + escapeHtml(product.title) + '</h4>' +
            '</a>' +
            ratingHtml +
            '<div class="ugc-product-pricing">' + pricingHtml + '</div>' +
          '</div>' +
        '</div>' +
        (variantSelector ? '<div class="ugc-variant-selector-wrap">' + variantSelector + '</div>' : '') +
        '<form class="ugc-modal-atc-form" data-product-handle="' + escapeAttribute(product.handle) + '">' +
          '<input type="hidden" name="id" value="' + escapeAttribute(variant.id) + '" class="ugc-variant-id-input">' +
          '<div data-ugc-primary-purchase>' + buttonHtml + '</div>' +
        '</form>' +
        '<div class="ugc-accelerated-checkout" data-ugc-accelerated-checkout hidden></div>' +
        reviewPreviewHtml +
        '<a href="' + escapeAttribute(productUrl) + '" class="ugc-view-details-link">View full details &#8594;</a>' +
      '</div>';

    bindProductEvents(modal, product);
    showProductContent(modal);
    renderRatingSummary(modal, cardData.rating, cardData.ratingCount, cardData.ratingScaleMax);

    activateOfficialPaymentButton(modal, product, variant);

    dispatchUGCEvent('ugc:product:rendered', {
      product: product,
      variant: variant,
      modal: modal.root,
      requestId: requestId
    });
  }

  function getProductImage(product, variant) {
    if (variant && variant.featured_image && variant.featured_image.src) {
      return normalizeShopifyImageUrl(variant.featured_image.src);
    }

    if (product.featured_image) {
      if (typeof product.featured_image === 'string') {
        return normalizeShopifyImageUrl(product.featured_image);
      }
      if (product.featured_image.src) {
        return normalizeShopifyImageUrl(product.featured_image.src);
      }
    }

    if (product.images && product.images.length) {
      var firstImage = product.images[0];
      if (typeof firstImage === 'string') {
        return normalizeShopifyImageUrl(firstImage);
      }
      if (firstImage.src) {
        return normalizeShopifyImageUrl(firstImage.src);
      }
    }

    return '';
  }

  function normalizeShopifyImageUrl(url) {
    if (!url) return '';
    if (url.indexOf('//') === 0) {
      return 'https:' + url;
    }
    return url;
  }


  /* =========================================================
     VARIANT
  ========================================================= */

  function findVariant(product, variantId) {
    if (!product || !product.variants || !product.variants.length) {
      return null;
    }

    if (variantId) {
      for (var i = 0; i < product.variants.length; i++) {
        if (String(product.variants[i].id) === String(variantId)) {
          return product.variants[i];
        }
      }
    }

    for (var j = 0; j < product.variants.length; j++) {
      if (product.variants[j].available) {
        return product.variants[j];
      }
    }

    return product.variants[0];
  }

  function buildVariantSelector(variants, currentId) {
    if (!variants || variants.length <= 1) {
      return '';
    }

    var options = '';
    for (var i = 0; i < variants.length; i++) {
      var variant = variants[i];
      var selected = String(variant.id) === String(currentId) ? ' selected' : '';
      var disabled = variant.available ? '' : ' disabled';
      var label = escapeHtml(variant.title) + (variant.available ? '' : ' — Sold out');

      options += '<option value="' + escapeAttribute(variant.id) + '"' + selected + disabled + '>' + label + '</option>';
    }

    return '<select class="ugc-variant-select" aria-label="Select product variant">' + options + '</select>';
  }

  function bindProductEvents(modal, product) {
    var content = modal.productContent;
    if (!content) return;

    var selector = content.querySelector('.ugc-variant-select');
    if (selector) {
      selector.addEventListener('change', function (event) {
        var selected = findVariant(product, event.target.value);
        if (selected) {
          updateVariantUI(modal, product, selected);
        }
      });
    }

    var form = content.querySelector('.ugc-modal-atc-form');
    if (form) {
      form.addEventListener('submit', handleAddToCart);
    }
  }

  function updateVariantUI(modal, product, variant) {
    var content = modal.productContent;
    if (!content) return;

    var input = content.querySelector('.ugc-variant-id-input');
    if (input) {
      input.value = variant.id;
    }

    var pricing = content.querySelector('.ugc-product-pricing');
    if (pricing) {
      pricing.innerHTML = buildPricingHtml(variant);
    }

    var purchase = content.querySelector('[data-ugc-primary-purchase]');
    if (purchase) {
      purchase.innerHTML = buildPurchaseButton(variant);
    }

    var image = content.querySelector('.ugc-product-image');
    if (image && variant.featured_image && variant.featured_image.src) {
      image.src = normalizeShopifyImageUrl(variant.featured_image.src);
    }

    var accelerated = content.querySelector('[data-ugc-accelerated-checkout]');
    if (accelerated) {
      accelerated.hidden = true;
    }

    activateOfficialPaymentButton(modal, product, variant);

    dispatchUGCEvent('ugc:variant:change', {
      product: product,
      variant: variant,
      modal: modal.root
    });
  }


  /* =========================================================
     PRICING
  ========================================================= */

  function buildPricingHtml(variant) {
    var price = Number(variant.price || 0);
    var compare = Number(variant.compare_at_price || 0);
    var hasDiscount = compare > price && price >= 0;

    var html = '<span class="ugc-current-price">' + escapeHtml(formatMoney(price)) + '</span>';

    if (hasDiscount) {
      html += '<span class="ugc-compare-price">' + escapeHtml(formatMoney(compare)) + '</span>';
      html += '<span class="ugc-save-badge">SAVE ' + escapeHtml(formatMoney(compare - price)) + '</span>';
    }

    return html;
  }


  /* =========================================================
     PURCHASE BUTTON
  ========================================================= */

  function buildPurchaseButton(variant) {
    if (!variant.available) {
      return '<button type="button" class="ugc-soldout-button" disabled>SOLD OUT</button>';
    }

    return (
      '<button type="submit" class="ugc-atc-button">' +
        '<span class="ugc-atc-text">ADD TO CART</span>' +
        '<span class="ugc-atc-spinner ugc-hidden" aria-hidden="true"></span>' +
      '</button>'
    );
  }


  /* =========================================================
     ADD TO CART
  ========================================================= */

  async function handleAddToCart(event) {
    event.preventDefault();

    var form = event.currentTarget;
    var input = form.querySelector('.ugc-variant-id-input');
    var button = form.querySelector('.ugc-atc-button');

    if (!input || !button) return;

    var variantId = input.value;
    if (!variantId) return;

    var text = button.querySelector('.ugc-atc-text');
    var spinner = button.querySelector('.ugc-atc-spinner');

    button.disabled = true;
    button.classList.add('is-loading');

    if (text) {
      text.textContent = 'ADDING...';
    }

    if (spinner) {
      spinner.classList.remove('ugc-hidden');
    }

    try {
      var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/';

      var response = await fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }]
        })
      });

      if (!response.ok) {
        var errorData = null;
        try {
          errorData = await response.json();
        } catch (_) {}

        throw new Error(
          errorData && (errorData.description || errorData.message)
            ? (errorData.description || errorData.message)
            : 'HTTP ' + response.status
        );
      }

      var result = await response.json();

      button.classList.remove('is-loading');
      button.classList.add('ugc-atc-success');

      if (spinner) {
        spinner.classList.add('ugc-hidden');
      }

      if (text) {
        text.textContent = 'ADDED!';
      }

      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: result }));
      document.dispatchEvent(new CustomEvent('on:cart:add', { bubbles: true, detail: result }));
      document.dispatchEvent(new CustomEvent('ugc:cart:add', {
        bubbles: true,
        detail: {
          variantId: variantId,
          productHandle: form.dataset.productHandle || '',
          response: result
        }
      }));

      window.setTimeout(function () {
        if (!document.documentElement.contains(button)) {
          return;
        }
        button.disabled = false;
        button.classList.remove('ugc-atc-success');
        if (text) {
          text.textContent = 'ADD TO CART';
        }
      }, 2200);

    } catch (error) {
      console.error('[UGC Modal] Add to cart failed:', error);

      button.disabled = false;
      button.classList.remove('is-loading');

      if (spinner) {
        spinner.classList.add('ugc-hidden');
      }

      if (text) {
        text.textContent = 'ADD TO CART';
      }

      showInlinePurchaseError(form, error.message || 'Unable to add this item.');
    }
  }

  function showInlinePurchaseError(form, message) {
    var old = form.querySelector('.ugc-purchase-error');
    if (old) {
      old.remove();
    }

    var error = document.createElement('p');
    error.className = 'ugc-purchase-error';
    error.setAttribute('role', 'alert');
    error.textContent = message || 'Unable to add this item to your cart.';

    form.appendChild(error);
  }


  /* =========================================================
     SHOPIFY OFFICIAL PAYMENT BUTTON
  ========================================================= */

  function activateOfficialPaymentButton(modal, product, variant) {
    if (!modal || !modal.root || !product || !variant) return;

    var acceleratedCheckout = modal.productContent ? modal.productContent.querySelector('[data-ugc-accelerated-checkout]') : null;

    if (!acceleratedCheckout) return;

    acceleratedCheckout.innerHTML = '';
    acceleratedCheckout.hidden = true;

    var currentProductId = modal.root.getAttribute('data-current-product-id');

    if (!currentProductId) {
      return;
    }

    var openedProductId = String(product.id || '');

    if (openedProductId !== currentProductId) {
      return;
    }

    var template = document.getElementById('ugc-payment-button-template');

    if (!template) {
      return;
    }

    var templateProductId = template.getAttribute('data-product-id');

    if (String(templateProductId) !== currentProductId) {
      return;
    }

    var templateContent = template.content || template;
    var clonedForm = templateContent.cloneNode(true);

    if (!clonedForm.querySelector) {
      var tempDiv = document.createElement('div');
      tempDiv.innerHTML = template.innerHTML;
      clonedForm = tempDiv;
    }

    var form = clonedForm.querySelector('form');

    if (!form) {
      return;
    }

    var variantInput = form.querySelector('[data-ugc-payment-variant-input]');

    if (variantInput) {
      variantInput.value = variant.id;
    }

    form.removeAttribute('id');

    acceleratedCheckout.appendChild(clonedForm);
    acceleratedCheckout.hidden = false;

    if (window.Shopify && window.Shopify.PaymentButton && typeof window.Shopify.PaymentButton.init === 'function') {
      try {
        window.Shopify.PaymentButton.init();
      } catch (error) {
        console.warn('[UGC Modal] Payment button init failed:', error);
      }
    }

    dispatchUGCEvent('ugc:payment:activated', {
      product: product,
      variant: variant
    });
  }


  /* =========================================================
     REVIEW EXPERIENCE
  ========================================================= */

  async function hydrateReviewExperience(modal, product, cardData, requestId) {
    if (!product) return;

    renderRatingSummary(modal, cardData.rating, cardData.ratingCount, cardData.ratingScaleMax);

    var reviewData = await loadProductReviews(product, cardData);

    if (requestId !== requestSerial || !isModalOpen()) {
      return;
    }

    if (!reviewData) {
      return;
    }

    var rating = cardData.rating || reviewData.rating;
    var count = cardData.ratingCount || reviewData.count;
    var scale = cardData.ratingScaleMax || reviewData.scaleMax || 5;

    renderRatingSummary(modal, rating, count, scale);
    renderReviewPreview(modal, product, reviewData);
  }


  /* =========================================================
     REVIEW ADAPTER SYSTEM
  ========================================================= */

  async function loadProductReviews(product, cardData) {
    var customAdapters = Array.isArray(window.UGCReviewAdapters) ? window.UGCReviewAdapters : [];

    var adapters = customAdapters.concat([loadReviewsFromCurrentProductDOM]);

    var context = {
      product: product,
      productId: cardData.productId || String(product.id || ''),
      productHandle: product.handle || '',
      limit: window.innerWidth <= MOBILE_BREAKPOINT ? 1 : 2
    };

    for (var i = 0; i < adapters.length; i++) {
      var adapter = adapters[i];
      if (typeof adapter !== 'function') {
        continue;
      }

      try {
        var data = await adapter(context);
        data = normalizeReviewData(data, context.limit);

        if (data && data.reviews && data.reviews.length) {
          return data;
        }
      } catch (error) {
        console.warn('[UGC Reviews] Adapter failed:', error);
      }
    }

    return null;
  }


  /* =========================================================
     CURRENT PDP DOM REVIEW ADAPTER
  ========================================================= */

  async function loadReviewsFromCurrentProductDOM(context) {
    if (!isCurrentProductPage(context.productHandle)) {
      return null;
    }

    var selectors = [
      '.jdgm-rev',
      '.loox-review',
      '.yotpo-review',
      '[data-review-id]',
      '.okeReviews-reviews-review',
      '.stamped-review',
      '.spr-review'
    ];

    var nodes = [];

    for (var s = 0; s < selectors.length; s++) {
      var found = document.querySelectorAll(selectors[s]);
      if (found.length) {
        nodes = Array.prototype.slice.call(found);
        break;
      }
    }

    if (!nodes.length) {
      return null;
    }

    var reviews = [];

    for (var i = 0; i < nodes.length; i++) {
      var review = extractReviewFromNode(nodes[i]);

      if (review && review.body) {
        reviews.push(review);
      }

      if (reviews.length >= context.limit) {
        break;
      }
    }

    if (!reviews.length) {
      return null;
    }

    var aggregate = readAggregateRatingFromDOM();

    return {
      rating: aggregate.rating,
      count: aggregate.count,
      scaleMax: 5,
      reviews: reviews,
      source: 'page-dom'
    };
  }

  function extractReviewFromNode(node) {
    var body = firstText(node, [
      '.jdgm-rev__body',
      '.loox-review-content',
      '.yotpo-read-more-text',
      '.content-review',
      '.okeReviews-review-main-content-body',
      '.stamped-review-content-body',
      '.spr-review-content-body',
      '[data-review-content]'
    ]);

    if (!body) {
      return null;
    }

    var title = firstText(node, [
      '.jdgm-rev__title',
      '.yotpo-review-title',
      '.okeReviews-review-main-content-title',
      '.stamped-review-header-title',
      '.spr-review-header-title'
    ]);

    var author = firstText(node, [
      '.jdgm-rev__author',
      '.loox-review-author',
      '.yotpo-user-name',
      '.okeReviews-review-reviewer-profile-name',
      '.stamped-review-header-by',
      '.spr-review-header-byline'
    ]);

    var rating = readRatingFromReviewNode(node);
    var verified = detectVerifiedReview(node);

    return {
      rating: rating,
      title: truncateText(title, 90),
      body: truncateText(body, 280),
      author: truncateText(author, 60),
      verified: verified
    };
  }

  function readRatingFromReviewNode(node) {
    var candidates = [
      node.getAttribute('data-score'),
      node.getAttribute('data-rating')
    ];

    var ratingEl = node.querySelector('[data-score], [data-rating], [aria-label*="star"], [aria-label*="Star"]');

    if (ratingEl) {
      candidates.push(ratingEl.getAttribute('data-score'));
      candidates.push(ratingEl.getAttribute('data-rating'));

      var aria = ratingEl.getAttribute('aria-label');
      if (aria) {
        var match = aria.match(/([0-5](?:\.\d+)?)/);
        if (match) {
          candidates.push(match[1]);
        }
      }
    }

    for (var i = 0; i < candidates.length; i++) {
      var parsed = parsePositiveFloat(candidates[i]);
      if (parsed && parsed <= 5) {
        return parsed;
      }
    }

    return null;
  }

  function detectVerifiedReview(node) {
    var selectors = [
      '.jdgm-rev__buyer-badge',
      '.yotpo-verified-buyer',
      '.okeReviews-review-reviewer-profile-status--verified',
      '.stamped-verified-badge'
    ];

    for (var i = 0; i < selectors.length; i++) {
      if (node.querySelector(selectors[i])) {
        return true;
      }
    }

    return false;
  }

  function readAggregateRatingFromDOM() {
    var ratingSelectors = [
      '.jdgm-prev-badge__stars',
      '[data-average-rating]',
      '[data-rating]',
      '.okeReviews-reviewsSummary-starRating',
      '.stamped-summary-text-1'
    ];

    var countSelectors = [
      '.jdgm-prev-badge__text',
      '[data-review-count]',
      '.yotpo-reviews-nav-tab',
      '.okeReviews-reviewsSummary-ratingCount',
      '.stamped-summary-caption'
    ];

    var rating = null;
    var count = null;

    for (var i = 0; i < ratingSelectors.length; i++) {
      var el = document.querySelector(ratingSelectors[i]);
      if (!el) continue;

      rating = parseRatingFromElement(el);
      if (rating) break;
    }

    for (var j = 0; j < countSelectors.length; j++) {
      var countEl = document.querySelector(countSelectors[j]);
      if (!countEl) continue;

      count = parseIntegerFromText(countEl.getAttribute('data-review-count') || countEl.textContent);
      if (count) break;
    }

    return {
      rating: rating,
      count: count
    };
  }

  function parseRatingFromElement(element) {
    var values = [
      element.getAttribute('data-average-rating'),
      element.getAttribute('data-rating'),
      element.getAttribute('data-score'),
      element.getAttribute('aria-label'),
      element.textContent
    ];

    for (var i = 0; i < values.length; i++) {
      if (!values[i]) continue;

      var match = String(values[i]).match(/([0-5](?:\.\d+)?)/);
      if (!match) continue;

      var rating = Number(match[1]);
      if (rating > 0 && rating <= 5) {
        return rating;
      }
    }

    return null;
  }

  function normalizeReviewData(data, limit) {
    if (!data || !Array.isArray(data.reviews)) {
      return null;
    }

    var reviews = [];

    for (var i = 0; i < data.reviews.length; i++) {
      var review = data.reviews[i];
      if (!review) continue;

      var body = cleanText(review.body || '');
      if (!body) continue;

      reviews.push({
        rating: parsePositiveFloat(review.rating),
        title: truncateText(cleanText(review.title || ''), 90),
        body: truncateText(body, 280),
        author: truncateText(cleanText(review.author || ''), 60),
        verified: review.verified === true
      });

      if (reviews.length >= limit) {
        break;
      }
    }

    if (!reviews.length) {
      return null;
    }

    return {
      rating: parsePositiveFloat(data.rating),
      count: parsePositiveInteger(data.count),
      scaleMax: parsePositiveFloat(data.scaleMax) || 5,
      reviews: reviews
    };
  }


  /* =========================================================
     RATING SUMMARY
  ========================================================= */

  function renderRatingSummary(modal, rating, count, scaleMax) {
    if (!modal.productContent) return;

    var hook = modal.productContent.querySelector('[data-ugc-rating-summary]');
    if (!hook) return;

    rating = parsePositiveFloat(rating);
    count = parsePositiveInteger(count);
    scaleMax = parsePositiveFloat(scaleMax) || 5;

    if (!rating || !count) {
      hook.hidden = true;
      hook.replaceChildren();
      return;
    }

    var normalized = Math.min(5, Math.max(0, rating / scaleMax * 5));

    hook.innerHTML =
      '<span class="ugc-rating-stars" aria-hidden="true">' + buildStars(normalized) + '</span>' +
      '<span class="ugc-rating-number">' + escapeHtml(formatRating(rating)) + '</span>' +
      '<span class="ugc-rating-count">(' + escapeHtml(formatInteger(count)) + ')</span>' +
      '<span class="visually-hidden">' + escapeHtml(formatRating(rating) + ' out of ' + scaleMax + ' stars, ' + count + ' reviews') + '</span>';

    hook.hidden = false;
  }


  /* =========================================================
     REVIEW PREVIEW
  ========================================================= */

  function renderReviewPreview(modal, product, data) {
    if (!modal.productContent) return;

    var hook = modal.productContent.querySelector('[data-ugc-review-preview]');
    if (!hook) return;

    if (!data || !data.reviews || !data.reviews.length) {
      hook.hidden = true;
      hook.replaceChildren();
      return;
    }

    var limit = window.innerWidth <= MOBILE_BREAKPOINT ? 1 : 2;
    var reviews = data.reviews.slice(0, limit);

    var html =
      '<div class="ugc-review-preview-header">' +
        '<span class="ugc-review-preview-heading">What customers are saying</span>' +
      '</div>' +
      '<div class="ugc-review-preview-list">';

    for (var i = 0; i < reviews.length; i++) {
      html += buildReviewCard(reviews[i]);
    }

    html += '</div>';

    var productUrl = '/products/' + encodeURIComponent(product.handle) + '#reviews';

    html +=
      '<a href="' + escapeAttribute(productUrl) + '" class="ugc-all-reviews-link">' +
        (data.count ? 'Read all ' + escapeHtml(formatInteger(data.count)) + ' reviews' : 'Read all reviews') +
        ' &#8594;' +
      '</a>';

    hook.innerHTML = html;
    hook.hidden = false;
  }

  function buildReviewCard(review) {
    var rating = review.rating && review.rating <= 5 ? review.rating : 5;

    var meta = '';

    if (review.author) {
      meta += '<span class="ugc-review-author">' + escapeHtml(review.author) + '</span>';
    }

    if (review.verified === true) {
      meta += '<span class="ugc-review-verified">Verified Buyer</span>';
    }

    return (
      '<article class="ugc-review-card">' +
        '<div class="ugc-review-stars" aria-label="' + escapeAttribute(rating + ' out of 5 stars') + '">' +
          buildStars(rating) +
        '</div>' +
        (review.title ? '<p class="ugc-review-title">' + escapeHtml(review.title) + '</p>' : '') +
        '<p class="ugc-review-body">&ldquo;' + escapeHtml(review.body) + '&rdquo;</p>' +
        (meta ? '<div class="ugc-review-meta">' + meta + '</div>' : '') +
      '</article>'
    );
  }

  function buildStars(rating) {
    var rounded = Math.round(Number(rating || 0));
    rounded = Math.min(5, Math.max(0, rounded));

    var output = '';
    for (var i = 1; i <= 5; i++) {
      output += '<span class="ugc-star' + (i <= rounded ? ' is-filled' : '') + '">&#9733;</span>';
    }

    return output;
  }


  /* =========================================================
     CURRENT PRODUCT DETECTION
  ========================================================= */

  function isCurrentProductPage(handle) {
    if (!handle) return false;

    var pathname = window.location.pathname.replace(/\/+$/, '');
    var expected = '/products/' + handle;

    return pathname === expected || pathname.endsWith(expected);
  }


  /* =========================================================
     LOADING UI
  ========================================================= */

  function showProductLoading(modal) {
    if (!modal) return;

    if (modal.productLoading) {
      modal.productLoading.hidden = false;
    }

    if (modal.productContent) {
      modal.productContent.hidden = true;
      modal.productContent.replaceChildren();
    }
  }

  function showProductContent(modal) {
    if (!modal) return;

    if (modal.productLoading) {
      modal.productLoading.hidden = true;
    }

    if (modal.productContent) {
      modal.productContent.hidden = false;
    }
  }


  /* =========================================================
     THEME EDITOR
  ========================================================= */

  function handleThemeSectionLoad() {
    currentModal = null;
    ensureModal();
  }

  function handleThemeSectionUnload(event) {
    var modal = ensureModal();
    if (!modal) return;

    if (event.target && event.target.contains(modal.root)) {
      destroyCurrentPlayer(modal.videoFrame);
      currentModal = null;
      document.body.classList.remove('ugc-body-no-scroll');
    }
  }


  /* =========================================================
     PUBLIC REVIEW ADAPTER API
  ========================================================= */

  if (!window.UGCReviewAdapters) {
    window.UGCReviewAdapters = [];
  }


  /* =========================================================
     EVENTS
  ========================================================= */

  function dispatchUGCEvent(eventName, detail) {
    try {
      document.dispatchEvent(new CustomEvent(eventName, {
        bubbles: true,
        detail: detail || {}
      }));
    } catch (_) {}
  }


  /* =========================================================
     UTILITIES
  ========================================================= */

  function formatMoney(cents) {
    if (cents === null || typeof cents === 'undefined') {
      return '';
    }

    var value = Number(cents);

    if (!Number.isFinite(value)) {
      return '';
    }

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try {
        return window.Shopify.formatMoney(value);
      } catch (_) {}
    }

    var moneyFormat = window.Shopify && window.Shopify.moneyFormat ? window.Shopify.moneyFormat : '${{amount}}';
    var amount = (value / 100).toFixed(2);

    return moneyFormat
      .replace(/\{\{\s*amount\s*\}\}/g, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.round(value / 100))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','));
  }

  function escapeHtml(value) {
    return String(value === null || typeof value === 'undefined' ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function escapeAttribute(value) {
    return escapeHtml(value);
  }

  function cleanText(value) {
    return String(value || '').replace(/\s+/g, ' ').trim();
  }

  function truncateText(text, maxLength) {
    text = cleanText(text);
    if (!text || text.length <= maxLength) {
      return text;
    }
    return text.slice(0, maxLength - 1).trim() + '…';
  }

  function firstText(root, selectors) {
    for (var i = 0; i < selectors.length; i++) {
      var element = root.querySelector(selectors[i]);
      if (!element) continue;

      var text = cleanText(element.textContent);
      if (text) {
        return text;
      }
    }
    return '';
  }

  function parsePositiveFloat(value) {
    if (value === null || typeof value === 'undefined' || value === '') {
      return null;
    }

    var parsed = parseFloat(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  function parsePositiveInteger(value) {
    if (value === null || typeof value === 'undefined' || value === '') {
      return null;
    }

    var parsed = parseInt(value, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  function parseIntegerFromText(value) {
    if (!value) return null;

    var normalized = String(value).replace(/,/g, '');
    var match = normalized.match(/(\d+)/);

    if (!match) {
      return null;
    }

    return parsePositiveInteger(match[1]);
  }

  function formatRating(value) {
    var number = Number(value);
    if (!Number.isFinite(number)) {
      return '';
    }
    return number.toFixed(1).replace(/\.0$/, '.0');
  }

  function formatInteger(value) {
    var number = Number(value);
    if (!Number.isFinite(number)) {
      return '';
    }

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US').format(number);
    } catch (_) {
      return String(number);
    }
  }


  /* =========================================================
     START
  ========================================================= */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

})();